import { campaignApi } from '@/api/feedback/campaignApi';
import { type Campaign, type CampaignSchedule } from '@/models/feedback/Campaign';
import { useQueries } from '@tanstack/react-query';
import { format, getDay, parse, startOfWeek } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { useState } from 'react';
import { Calendar, dateFnsLocalizer, Views, type Event, type View } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales: { 'en-US': enUS },
});

interface Props {
    campaigns: Campaign[];
    onSelectSchedule?: (schedule: CampaignSchedule, campaignId: string) => void;
}

const ScheduleCalendar = ({ campaigns, onSelectSchedule }: Props) => {
    // Controlled navigation/view state so the toolbar Back/Next and Month/Week/Day buttons work.
    const [date, setDate] = useState(new Date());
    const [view, setView] = useState<View>(Views.MONTH);

    // One query per campaign; combine every campaign's schedules into calendar events.
    const scheduleQueries = useQueries({
        queries: campaigns.map((c) => ({
            queryKey: ['feedback-schedules', c.id],
            queryFn: () => campaignApi.listSchedules(c.id),
            staleTime: 0,
        })),
    });

    const events: Event[] = [];
    scheduleQueries.forEach((query, index) => {
        const campaign = campaigns[index];
        (query.data ?? []).forEach((s) => {
            events.push({
                title: campaign.name,
                start: new Date(s.startDateTime),
                end: new Date(s.endDateTime),
                resource: { schedule: s, campaignId: campaign.id },
            });
        });
    });

    return (
        <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            date={date}
            onNavigate={setDate}
            view={view}
            onView={setView}
            views={[Views.MONTH, Views.WEEK, Views.DAY]}
            onSelectEvent={(event) => {
                const resource = event.resource as { schedule: CampaignSchedule; campaignId: string } | undefined;
                if (resource) onSelectSchedule?.(resource.schedule, resource.campaignId);
            }}
        />
    );
};

export default ScheduleCalendar;
